import { Fetch } from '../helpers/fetchWrapper';
import useServiceQuery from '../hooks/useServiceQuery';

const teamService = {
  _url: process.env.REACT_APP_WORKSPACE_API_URL,

  GetInvitedMembers(searchQuery, fetch, workspaceId) {
    const { data, loading, error } = useServiceQuery({
      name: 'team.invited',
      params: { searchQuery, workspaceId },
      queryFn: () => this.getInvitedMembers(searchQuery),
      refresh: fetch,
      enabled: Boolean(workspaceId),
    });
    return {
      invitedMembers_loading: loading,
      invitedMembers_error: error,
      invitedMembers_data: data?.data,
    };
  },

  GetAllMembers(searchQuery, fetch, workspaceId) {
    const { data, loading, error } = useServiceQuery({
      name: 'team.members',
      params: { searchQuery, workspaceId },
      queryFn: () => this.getAllMembers(searchQuery),
      refresh: fetch,
      enabled: Boolean(workspaceId),
    });
    return {
      allMembers_loading: loading,
      allMembers_error: error,
      allMembers_data: data?.data,
    };
  },

  async createWorkspace(payload) {
    let res = await Fetch.post(`${this._url}/create`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Failed to create workspace');
  },

  async inviteMember(payload, workspaceId) {
    let res = await Fetch.post(`${this._url}/invite/${workspaceId}`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Failed to send invite');
  },

  async updateMember(payload, workspaceId, memberId) {
    let res = await Fetch.put(`${this._url}/update-member/${workspaceId}/${memberId}`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Failed to update member');
  },

  async updateWorkspace(workspaceId, payload) {
    let res = await Fetch.put(`${this._url}/${workspaceId}`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Failed to update workspace');
  },

  async deleteWorkspace(workspaceId) {
    let res = await Fetch.delete(`${this._url}/${workspaceId}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Failed to delete workspace');
  },

  async removeMember(workspaceId, memberId) {
    let res = await Fetch.delete(`${this._url}/delete-member/${workspaceId}/${memberId}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Failed to remove member');
  },

  async getCurrentUserWorkspace() {
    let res = await Fetch.get(`${this._url}/get-all-workspaces`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addMember(payload, workspaceId) {
    let res = await Fetch.post(`${this._url}/add-members/${workspaceId}`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async checkUserAvailability(payload, workspaceId) {
    let res = await Fetch.post(`${this._url}/check-availability/${workspaceId}`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getInvitedMembers({
    page = 1,
    itemsPerPage = 10,
    searchText = '',
    getAll = false,
    startDate = '',
    endDate = '',
    workspaceId,
  }) {
    let res = await Fetch.get(
      `${this._url}/invited-members/${workspaceId}?page=${page}&itemsPerPage=${itemsPerPage}&searchText=${searchText}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async getAllMembers({
    page = 1,
    itemsPerPage = 10,
    searchText = '',
    getAll = false,
    startDate = '',
    endDate = '',
    workspaceId,
  }) {
    let res = await Fetch.get(
      `${this._url}/all-members/${workspaceId}?page=${page}&itemsPerPage=${itemsPerPage}&searchText=${searchText}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async verifyWorkspaceInvite(token) {
    let res = await Fetch.get(`${this._url}/verify-invite?token=${encodeURIComponent(token)}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Invalid or expired invitation');
  },

  async acceptWorkspaceInvite(payload) {
    let res = await Fetch.post(`${this._url}/accept-invite`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message, code } = await res.json();
    const error = new Error(message ?? 'Failed to accept invitation');
    // LOGIN_REQUIRED / WRONG_ACCOUNT: an existing account must sign in to accept.
    error.code = code;
    throw error;
  },
};

export default teamService;
